import { QrTemplate, QrTemplateColors, EditableSlot } from './types';

const SANS = '-apple-system, "Helvetica Neue", Helvetica, Arial, sans-serif';
const SERIF = 'Georgia, "Times New Roman", serif';

const slots: EditableSlot[] = [
  {
    id: 'eyebrow',
    label: 'Oberes Label',
    defaultValue: 'TERMIN  —  24/7',
    modeVariants: { whatsapp: 'TERMIN  —  24/7', vcard: 'KONTAKT  —  24/7' },
    positionable: true,
  },
  {
    id: 'hero',
    label: 'Headline',
    defaultValue: 'Termine per Scan.',
    modeVariants: { whatsapp: 'Termine per Scan.', vcard: 'Als Kontakt.' },
    positionable: true,
  },
  {
    id: 'sub',
    label: 'Unterzeile',
    defaultValue: 'Rund um die Uhr — per WhatsApp oder Anruf.',
    modeVariants: {
      whatsapp: 'Rund um die Uhr — per WhatsApp oder Anruf.',
      vcard: 'Einmal scannen. Immer griffbereit.',
    },
    positionable: true,
  },
  {
    id: 'cta',
    label: 'Call-to-Action',
    defaultValue: 'Jetzt Termin buchen',
    modeVariants: { whatsapp: 'Jetzt Termin buchen', vcard: 'Kontakt speichern' },
    positionable: true,
  },
  {
    id: 'footer',
    label: 'Footer-Tag',
    defaultValue: 'WHATSAPP  •  ANRUF  •  24/7',
    modeVariants: {
      whatsapp: 'WHATSAPP  •  ANRUF  •  24/7',
      vcard: 'EINFACH  •  SCANNEN  •  SPEICHERN',
    },
    positionable: true,
  },
];

export const flyerA4: QrTemplate = {
  id: 'flyer-a4',
  name: 'A4 Flyer',
  description: 'Editorialer A4-Flyer zum Auslegen oder Aushängen – eine klare Hierarchie, ein starker QR-Code.',
  format: 'A4 Hochformat, 300 dpi (2480×3508 px)',
  canvasWidth: 2480,
  canvasHeight: 3508,
  slots,

  defaultColors: {
    background: '#F6F1E8',
    surface:    '#FFFFFF',
    text:       '#1C1A18',
    textMuted:  '#8A827A',
    accent:     '#C9A96E',
    onAccent:   '#1C1A18',
  } satisfies QrTemplateColors,

  render: ({ ctx, canvas, qrImage, business, colors, slotValues, slotOffsets }) => {
    const W = canvas.width;
    const H = canvas.height;
    const CX = W / 2;
    const SIDE = 220;
    const { background, text, textMuted, accent } = colors;
    const WHITE = '#FFFFFF';

    ctx.fillStyle = background;
    ctx.fillRect(0, 0, W, H);

    ctx.fillStyle = accent;
    ctx.fillRect(SIDE, 80, W - SIDE * 2, 4);
    ctx.fillRect(SIDE, H - 80 - 4, W - SIDE * 2, 4);

    const eyeOff = slotOffsets.eyebrow;
    const eyebrowY = 260 + eyeOff.y;
    ctx.save();
    ctx.font = `500 40px ${SANS}`;
    (ctx as any).letterSpacing = '16px';
    ctx.fillStyle = accent;
    ctx.textAlign = 'center';
    ctx.fillText(slotValues.eyebrow, CX + 8 + eyeOff.x, eyebrowY);
    (ctx as any).letterSpacing = '0px';
    ctx.restore();

    const nameY = 400;
    let nameSize = 110;
    const nameMaxW = W - SIDE * 2 - 60;
    ctx.font = `700 ${nameSize}px ${SERIF}`;
    while (ctx.measureText(business.name).width > nameMaxW && nameSize > 60) {
      nameSize -= 4;
      ctx.font = `700 ${nameSize}px ${SERIF}`;
    }
    ctx.save();
    ctx.fillStyle = text;
    ctx.textAlign = 'center';
    ctx.fillText(business.name, CX, nameY);
    ctx.restore();

    ctx.fillStyle = accent;
    ctx.fillRect(CX - 80, 500, 160, 2);

    const heroOff = slotOffsets.hero;
    const heroY = 880 + heroOff.y;
    ctx.save();
    ctx.font = `900 280px ${SERIF}`;
    ctx.fillStyle = text;
    ctx.textAlign = 'center';
    ctx.fillText(slotValues.hero, CX + heroOff.x, heroY);
    ctx.restore();

    const subOff = slotOffsets.sub;
    const subY = 1050 + subOff.y;
    ctx.save();
    ctx.font = `italic 300 64px ${SERIF}`;
    ctx.fillStyle = textMuted;
    ctx.textAlign = 'center';
    ctx.fillText(slotValues.sub, CX + subOff.x, subY);
    ctx.restore();

    const QR_SIZE = 1100;
    const QR_PAD = 80;
    const QR_TILE = QR_SIZE + QR_PAD * 2;
    const qrX = CX - QR_TILE / 2;
    const qrY = 1260;

    ctx.save();
    ctx.shadowColor = 'rgba(28, 26, 24, 0.08)';
    ctx.shadowBlur = 40;
    ctx.shadowOffsetY = 12;
    ctx.fillStyle = WHITE;
    ctx.fillRect(qrX, qrY, QR_TILE, QR_TILE);
    ctx.restore();

    ctx.drawImage(qrImage, qrX + QR_PAD, qrY + QR_PAD, QR_SIZE, QR_SIZE);

    const qrBottom = qrY + QR_TILE;

    const ctaOff = slotOffsets.cta;
    const ctaY = qrBottom + 160 + ctaOff.y;
    ctx.save();
    ctx.font = `700 88px ${SERIF}`;
    ctx.fillStyle = text;
    ctx.textAlign = 'center';
    ctx.fillText(slotValues.cta, CX + ctaOff.x, ctaY);
    ctx.restore();

    const phone = business.whatsappNumber.startsWith('+')
      ? business.whatsappNumber
      : '+' + business.whatsappNumber.replace(/\D/g, '');
    const phoneY = ctaY + 100;
    ctx.save();
    ctx.font = `400 58px ${SANS}`;
    (ctx as any).letterSpacing = '4px';
    ctx.fillStyle = textMuted;
    ctx.textAlign = 'center';
    ctx.fillText(phone, CX + 2, phoneY);
    (ctx as any).letterSpacing = '0px';
    ctx.restore();

    ctx.fillStyle = accent;
    ctx.fillRect(CX - 80, phoneY + 100, 160, 2);

    const footerOff = slotOffsets.footer;
    const footerY = phoneY + 220 + footerOff.y;
    ctx.save();
    ctx.font = `400 42px ${SANS}`;
    (ctx as any).letterSpacing = '8px';
    ctx.fillStyle = textMuted;
    ctx.textAlign = 'center';
    ctx.fillText(slotValues.footer, CX + 4 + footerOff.x, footerY);
    (ctx as any).letterSpacing = '0px';
    ctx.restore();
  },
};
